import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ArrowUpIcon, ArrowDownIcon, CheckIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import MetricsChart from '../shared/MetricsChart';

// Add API base URL constant
// const API_BASE_URL = 'http://localhost:5669/streaming';
const API_BASE_URL = process.env.REACT_APP_API_URL + '/streaming';

function formatNumber(num) {
  return new Intl.NumberFormat().format(num);
}

// Compact human format for mobile: 1.2K / 3.4M / 1.1B (1 decimal, trimmed).
function formatCompact(num) {
  const n = Number(num) || 0;
  const abs = Math.abs(n);
  const fmt = (v, suffix) => `${v.toFixed(1).replace(/\.0$/, '')}${suffix}`;
  if (abs >= 1e9) return fmt(n / 1e9, 'B');
  if (abs >= 1e6) return fmt(n / 1e6, 'M');
  if (abs >= 1e3) return fmt(n / 1e3, 'K');
  return String(n);
}

function formatPercent(num, showPlus = false) {
  if (num === null || num === undefined) return '';
  return `${showPlus && num > 0 ? '+' : ''}${num.toFixed(1)}%`;
}

// V2: compact % change pill (green up / red down / neutral), intensity scaling
// with magnitude. Cleaner than tinting the whole cell.
function getPercentPill(percent) {
  if (percent === null || percent === undefined) return 'bg-gray-100 text-gray-400';
  const m = Math.abs(percent);
  if (percent > 0) {
    if (m < 5) return 'bg-green-50 text-green-700';
    if (m < 25) return 'bg-green-100 text-green-700';
    return 'bg-green-200 text-green-800';
  }
  if (percent < 0) {
    if (m < 5) return 'bg-red-50 text-red-700';
    if (m < 25) return 'bg-red-100 text-red-700';
    return 'bg-red-200 text-red-800';
  }
  return 'bg-gray-100 text-gray-500';
}

// Reusable pill cell for a % change value.
function PercentPill({ value }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold tabular-nums ${getPercentPill(value)}`}>
      {value === null || value === undefined ? '–' : `${value > 0 ? '+' : ''}${value.toFixed(1)}%`}
    </span>
  );
}

// Dropdown for sorting a merged streams/% column — pick which metric. First
// pick sorts desc; picking the active one again toggles to asc.
function MergedSortMenu({ anchorRect, options, sortConfig, onSelect, onClose }) {
  const menuRef = React.useRef(null);
  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const WIDTH = 184;
  const top = anchorRect.bottom + 4;
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - WIDTH - 8));

  return createPortal(
    <div
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top, left, width: WIDTH, zIndex: 50 }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 normal-case tracking-normal"
    >
      {options.map((o) => {
        const active = sortConfig?.key === o.key;
        const nextOrder = active && sortConfig?.order === 'desc' ? 'asc' : 'desc';
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onSelect(o.key, nextOrder)}
            className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs hover:bg-blue-50 ${active ? 'text-blue-600 font-semibold bg-blue-50/60' : 'text-gray-700'}`}
          >
            <span>{o.label}</span>
            {active && (
              sortConfig?.order === 'desc'
                ? <ArrowDownIcon className="h-3.5 w-3.5" />
                : <ArrowUpIcon className="h-3.5 w-3.5" />
            )}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

export default function StreamingSummaryTable({ type, audioOnly = false, group = null, isPgp = false, onAddClick = null }) {
  const [expandedRow, setExpandedRow] = useState(null);
  // const [expandedItem, setExpandedItem] = useState(null);
  const [artistFilter, setArtistFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [timeRange, setTimeRange] = useState(30);
  const [sortConfig, setSortConfig] = useState({ key: 'last_complete_week_streams', order: 'desc' });
  const [selectedMetrics, setSelectedMetrics] = useState(['audio']);
  const [isMetricsOpen, setIsMetricsOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('global');
  const [showExportModal, setShowExportModal] = useState(false);
  const [expandedRowTab, setExpandedRowTab] = useState('metrics'); // 'metrics' or 'songs'
  const [artistSongsSortConfig, setArtistSongsSortConfig] = useState({ key: 'global_last_complete_week_streams', order: 'desc' });
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'

  // Metric options
  const metricOptions = [
    { id: 'total', label: 'Total Streams' },
    { id: 'audio', label: 'Audio Streams' },
    { id: 'video', label: 'Video Streams' },
    { id: 'ad', label: 'Ad-Supported' },
    { id: 'premium', label: 'Premium' },
    { id: 'ondemand', label: 'On-Demand' },
    { id: 'programmed', label: 'Programmed' }
  ];

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      }
      return [...prev, metricId];
    });
  };

  // Get the label of the first selected metric for display
  const selectedMetricLabel = metricOptions.find(opt => selectedMetrics.includes(opt.id))?.label || 'Select Type';

  // Debounce artist filter
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(artistFilter), 500);
    return () => clearTimeout(timer);
  }, [artistFilter]);

  // Reset expanded row tab when expanding a different row
  useEffect(() => {
    setExpandedRowTab('metrics');
    setArtistSongsSortConfig({ key: 'global_last_complete_week_streams', order: 'desc' }); // Reset sort when changing rows
  }, [expandedRow]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(
    ['summaries', type, debouncedFilter, sortConfig, selectedRegion, audioOnly, group, isPgp],
    async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        per_page: 20,
        artist: debouncedFilter || undefined,
        sort_by: sortConfig.key,
        sort_order: sortConfig.order,
        region: selectedRegion,
        audio_only: audioOnly
      };
      
      // Add group parameter if provided
      if (group) {
        params.group = group;
      }
      
      // Add is_pgp parameter if provided
      if (isPgp) {
        params.is_pgp = 'true';
      }
      
      const response = await axios.get(`${API_BASE_URL}/${type}-summaries`, {
        params
      });
      
      console.log('API Response:', response.data);
      return response.data;
    },
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.page < Math.ceil(lastPage.total / lastPage.per_page)
          ? lastPage.page + 1
          : undefined;
      }
    }
  );

  const { data: metrics } = useQuery(
    ['metrics', type, expandedRow, timeRange, selectedRegion, viewMode],
    async () => {
      if (!expandedRow || (type === 'artist' && expandedRowTab !== 'metrics')) return null;
      let endpoint;
      if (viewMode === 'weekly') {
        // For artists, use the on-catalog endpoint for weekly metrics
        if (type === 'artist') {
          endpoint = `${API_BASE_URL}/artist-metrics-weekly-on-catalog/${expandedRow}`;
        } else if (type === 'album') {
          endpoint = `${API_BASE_URL}/album-metrics-weekly/${expandedRow}`;
        } else {
          endpoint = `${API_BASE_URL}/song-metrics-weekly/${expandedRow}`;
        }
      } else {
        endpoint = `${API_BASE_URL}/${type}-metrics/${expandedRow}`;
      }
      const response = await axios.get(endpoint, {
        params: {
          days: timeRange,
          region: selectedRegion
        }
      });
      console.log('Metrics response:', response.data);
      return response.data;
    },
    { enabled: !!(expandedRow && (type === 'song' || type === 'album' || (type === 'artist' && expandedRowTab === 'metrics'))) }
  );

  // Query for artist songs when in artist view and songs tab is selected
  const { data: artistSongs } = useQuery(
    ['artist-songs', expandedRow, debouncedFilter, artistSongsSortConfig],
    async () => {
      if (!expandedRow || type !== 'artist' || expandedRowTab !== 'songs') return null;
      
      // Get the artist name from the current data
      const artist = data?.pages
        .flatMap(page => page.data)
        .find(item => item.id === expandedRow);
      
      if (!artist) return null;
      
      const response = await axios.get(`${API_BASE_URL}/song-summaries`, {
        params: {
          artist: artist.name,
          per_page: 50, // Get more songs for the artist
          sort_by: artistSongsSortConfig.key,
          sort_order: artistSongsSortConfig.order
        }
      });
      return response.data;
    },
    { enabled: !!(expandedRow && type === 'artist' && expandedRowTab === 'songs') }
  );

  // Debug log for selected metrics
  useEffect(() => {
    if (metrics && expandedRow) {
      console.log('Selected metrics:', selectedMetrics);
      console.log('Available metrics:', metrics);
    }
  }, [metrics, selectedMetrics, expandedRow]);

  // Intersection Observer for infinite scroll
  const observerTarget = React.useRef(null);
  // V2: track scroll container width so the expanded row can be pinned to the
  // viewport (stays put while the table scrolls sideways). A callback ref is
  // used (not a mount effect) because the scroll container mounts AFTER the
  // initial loading-spinner render — a [] effect would run while the ref is
  // still null and never re-run, leaving width at 0.
  const resizeObserverRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const setScrollContainer = React.useCallback((el) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    ro.observe(el);
    resizeObserverRef.current = ro;
  }, []);
  useEffect(() => () => {
    if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
  }, []);
  // V2: sticky style for the pinned first column (artist/name).
  const pinnedHeaderStyle = { position: 'sticky', left: 0, zIndex: 11, background: '#f1f5f9', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const pinnedCellStyle = { position: 'sticky', left: 0, zIndex: 5, background: 'white', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  // V2: mobile breakpoint (Tailwind `sm` = 640px). On mobile, song/album tables
  // merge the artist + title columns into a single pinned column.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const mergeTitleIntoArtist = isMobile && (type === 'song' || type === 'album');
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleSort = (key, explicitOrder = null) => {
    setSortConfig(prev => ({
      key,
      order: explicitOrder || (prev.key === key && prev.order === 'desc' ? 'asc' : 'desc')
    }));
  };

  // Merged streams/% sort dropdown (mobile).
  const [sortMenu, setSortMenu] = useState(null); // { options, rect }
  const openMergedSortMenu = (options, e) => {
    e.stopPropagation();
    if (sortMenu && sortMenu.options[0].key === options[0].key) {
      setSortMenu(null);
    } else {
      setSortMenu({ options, rect: e.currentTarget.getBoundingClientRect() });
    }
  };
  const applyMergedSort = (key, order) => {
    handleSort(key, order);
    setSortMenu(null);
  };

  const handleArtistSongsSort = (key, explicitOrder = null) => {
    setArtistSongsSortConfig(prev => ({
      key,
      order: explicitOrder || (prev.key === key && prev.order === 'desc' ? 'asc' : 'desc')
    }));
  };

  // Merged streams/% sort dropdown for the artist-songs sub-table (mobile).
  const [songsSortMenu, setSongsSortMenu] = useState(null);
  const openSongsSortMenu = (options, e) => {
    e.stopPropagation();
    if (songsSortMenu && songsSortMenu.options[0].key === options[0].key) {
      setSongsSortMenu(null);
    } else {
      setSongsSortMenu({ options, rect: e.currentTarget.getBoundingClientRect() });
    }
  };
  const applySongsSort = (key, order) => {
    handleArtistSongsSort(key, order);
    setSongsSortMenu(null);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.order === 'desc' ? 
      <ArrowDownIcon className="h-4 w-4 inline ml-1" /> : 
      <ArrowUpIcon className="h-4 w-4 inline ml-1" />;
  };

  const ArtistSongsSortIcon = ({ columnKey }) => {
    if (artistSongsSortConfig.key !== columnKey) return null;
    return artistSongsSortConfig.order === 'desc' ? 
      <ArrowDownIcon className="h-4 w-4 inline ml-1" /> : 
      <ArrowUpIcon className="h-4 w-4 inline ml-1" />;
  };

  const handleExport = async (format) => {
    try {
      const params = {
        artist: debouncedFilter || undefined,
        sort_by: sortConfig.key,
        sort_order: sortConfig.order,
        format: format,
        audio_only: audioOnly,
        region: selectedRegion
      };
      
      // Add group parameter if provided
      if (group) {
        params.group = group;
      }
      
      // Add is_pgp parameter if provided
      if (isPgp) {
        params.is_pgp = 'true';
      }
      
      const response = await axios.get(`${API_BASE_URL}/${type}-export`, {
        responseType: 'blob',
        params
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileExtension = format === 'csv' ? 'csv' : 'pdf';
      link.setAttribute('download', `streaming_${type}s_${new Date().toISOString().split('T')[0]}.${fileExtension}`);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const handleRowClick = (item) => {
    if (expandedRow === item.id) {
      setExpandedRow(null);
      // setExpandedItem(null);
    } else {
      setExpandedRow(item.id);
      // setExpandedItem(item);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 sm:gap-4 px-2 sm:px-0">
        <input
          type="text"
          placeholder="Filter by artist name..."
          value={artistFilter}
          onChange={e => setArtistFilter(e.target.value)}
          className="flex-1 min-w-0 md:flex-none px-3 sm:px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 focus:bg-white transition md:w-auto"
        />
        <div className="flex items-center gap-2 shrink-0">
          {onAddClick && (
            <button
              onClick={onAddClick}
              className="inline-flex items-center px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg text-white bg-emerald-500 hover:bg-emerald-600 shadow-sm hover:shadow transition whitespace-nowrap"
            >
              <svg className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Add {type === 'album' ? 'Album' : type === 'song' ? 'Song' : 'Artist'}</span>
              <span className="sm:hidden ml-0.5">{type === 'album' ? 'Album' : type === 'song' ? 'Song' : 'Artist'}</span>
            </button>
          )}
          <button
            onClick={() => setShowExportModal(true)}
            className="inline-flex items-center px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow transition whitespace-nowrap"
          >
            <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-1" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Choose Export Format</h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export as CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="relative">
        <div className="overflow-x-auto min-w-full border-y sm:border border-gray-200/80 sm:rounded-xl" ref={setScrollContainer}>
          <table className="w-full">
            <colgroup>
              {(type === 'song' || type === 'album') && !mergeTitleIntoArtist ? (
                <>
                  <col className="w-[15%]" /> {/* Artist */}
                  <col className="w-[15%]" /> {/* Song/Album */}
                </>
              ) : (
                <col className="w-[15%]" /> /* Artist (or merged Artist/Title on mobile) */
              )}
              {/* Global: one merged col on mobile, two on desktop. */}
              <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} /> {/* Global (Last Week / merged) */}
              {!isMobile && <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} />} {/* Global % Change */}
              {!isPgp && (
                <>
                  <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} /> {/* US (Last Week / merged) */}
                  {!isMobile && <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} />} {/* US % Change */}
                </>
              )}
              {type === 'song' && (
                <col className="w-[12%]" />
              )}
              <col className={`hidden lg:table-cell ${(type === 'song' || type === 'album') ? 'w-[10%]' : 'w-[12%]'}`} />
              <col className={`hidden lg:table-cell ${(type === 'song' || type === 'album') ? 'w-[10%]' : 'w-[12%]'}`} />
            </colgroup>
            <thead className="bg-slate-50/80 sticky top-0 z-10">
              <tr className="border-b border-gray-200">
                {type === 'song' ? (
                  <>
                    <th
                      onClick={() => handleSort(mergeTitleIntoArtist ? 'title' : 'artist')}
                      style={pinnedHeaderStyle}
                      className="bg-slate-50/80 px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                    >
                      {mergeTitleIntoArtist ? 'Artist / Song' : 'Artist'}
                      <SortIcon columnKey={mergeTitleIntoArtist ? 'title' : 'artist'} />
                    </th>
                    {!mergeTitleIntoArtist && (
                      <th
                        onClick={() => handleSort('title')}
                        className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                      >
                        Song
                        <SortIcon columnKey="title" />
                      </th>
                    )}
                  </>
                ) : type === 'album' ? (
                  <>
                    <th
                      onClick={() => handleSort(mergeTitleIntoArtist ? 'title' : 'artist')}
                      style={pinnedHeaderStyle}
                      className="bg-slate-50/80 px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                    >
                      {mergeTitleIntoArtist ? 'Artist / Album' : 'Artist'}
                      <SortIcon columnKey={mergeTitleIntoArtist ? 'title' : 'artist'} />
                    </th>
                    {!mergeTitleIntoArtist && (
                      <th
                        onClick={() => handleSort('title')}
                        className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                      >
                        Album
                        <SortIcon columnKey="title" />
                      </th>
                    )}
                  </>
                ) : (
                  <th
                    onClick={() => handleSort('name')}
                    style={pinnedHeaderStyle}
                    className="bg-slate-50/80 px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                  >
                    Artist
                    <SortIcon columnKey="name" />
                  </th>
                )}
                {isMobile ? (
                  <th
                    onClick={(e) => openMergedSortMenu([
                      { key: 'global_last_complete_week_streams', label: 'Global Last Week' },
                      { key: 'global_week_over_week_change_percent', label: 'Global % Change' },
                    ], e)}
                    className="px-1 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  >
                    <span className="inline-flex items-center gap-1 justify-end">
                      Global
                      {(sortConfig.key === 'global_last_complete_week_streams' || sortConfig.key === 'global_week_over_week_change_percent') && (
                        sortConfig.order === 'desc' ? <ArrowDownIcon className="h-3 w-3 text-blue-600" /> : <ArrowUpIcon className="h-3 w-3 text-blue-600" />
                      )}
                      <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                    </span>
                  </th>
                ) : (
                  <>
                    <th onClick={() => handleSort('global_last_complete_week_streams')} className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                      Global Last Week
                      <SortIcon columnKey="global_last_complete_week_streams" />
                    </th>
                    <th onClick={() => handleSort('global_week_over_week_change_percent')} className="px-1 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                      Global % Change
                      <SortIcon columnKey="global_week_over_week_change_percent" />
                    </th>
                  </>
                )}
                {!isPgp && (
                  isMobile ? (
                    <th
                      onClick={(e) => openMergedSortMenu([
                        { key: 'us_last_complete_week_streams', label: 'US Last Week' },
                        { key: 'us_week_over_week_change_percent', label: 'US % Change' },
                      ], e)}
                      className="px-1 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        US
                        {(sortConfig.key === 'us_last_complete_week_streams' || sortConfig.key === 'us_week_over_week_change_percent') && (
                          sortConfig.order === 'desc' ? <ArrowDownIcon className="h-3 w-3 text-blue-600" /> : <ArrowUpIcon className="h-3 w-3 text-blue-600" />
                        )}
                        <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                      </span>
                    </th>
                  ) : (
                    <>
                      <th onClick={() => handleSort('us_last_complete_week_streams')} className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                        US Last Week
                        <SortIcon columnKey="us_last_complete_week_streams" />
                      </th>
                      <th onClick={() => handleSort('us_week_over_week_change_percent')} className="px-1 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                        US % Change
                        <SortIcon columnKey="us_week_over_week_change_percent" />
                      </th>
                    </>
                  )
                )}
                {type === 'song' && (
                  <th onClick={() => handleSort('atd_streams')} className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                    ATD
                    <SortIcon columnKey="atd_streams" />
                  </th>
                )}
                <th onClick={() => handleSort('audio_streams_percent')} className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                  Audio %
                  <SortIcon columnKey="audio_streams_percent" />
                </th>
                <th onClick={() => handleSort('video_streams_percent')} className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                  Video %
                  <SortIcon columnKey="video_streams_percent" />
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {data?.pages.map((page, i) => (
                <React.Fragment key={i}>
                  {page.data.map(item => (
                    <React.Fragment key={item.id}>
                      <tr
                        onClick={() => handleRowClick(item)}
                        className={`group transition-colors hover:bg-blue-50/40 cursor-pointer ${expandedRow === item.id ? 'bg-blue-50/60' : ''}`}
                      >
                        {(type === 'song' || type === 'album') && mergeTitleIntoArtist ? (
                          <td style={pinnedCellStyle} className="px-1 sm:px-4 py-2 sm:py-4 max-w-[150px]">
                            <div className="font-medium text-[11px] sm:text-sm leading-tight truncate" title={item.title}>{item.title}</div>
                            <div className="text-[10px] text-gray-500 leading-tight truncate" title={item.artist}>{item.artist}</div>
                          </td>
                        ) : type === 'song' ? (
                          <>
                            <td
                              style={pinnedCellStyle}
                              className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate`}
                            >
                              {item.artist}
                            </td>
                            <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate">
                              {item.title}
                            </td>
                          </>
                        ) : type === 'album' ? (
                          <>
                            <td
                              style={pinnedCellStyle}
                              className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate`}
                            >
                              {item.artist}
                            </td>
                            <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate">
                              {item.title}
                            </td>
                          </>
                        ) : (
                          <td
                            style={pinnedCellStyle}
                            className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate`}
                          >
                            {item.name}
                          </td>
                        )}
                        {isMobile ? (
                          <td className="px-1.5 py-1.5 text-right">
                            <div className="text-[12px] font-bold tabular-nums text-gray-900 leading-tight">{formatCompact(item.global_last_complete_week_streams)}</div>
                            <div className="mt-0.5"><PercentPill value={item.global_week_over_week_change_percent} /></div>
                          </td>
                        ) : (
                          <>
                            <td className="px-1 sm:px-4 py-2 sm:py-3.5 text-[11px] sm:text-sm text-right tabular-nums text-gray-800">
                              {formatNumber(item.global_last_complete_week_streams)}
                            </td>
                            <td className="px-1 sm:px-4 py-2 sm:py-3.5 text-center">
                              <PercentPill value={item.global_week_over_week_change_percent} />
                            </td>
                          </>
                        )}
                        {!isPgp && (
                          isMobile ? (
                            <td className="px-1.5 py-1.5 text-right">
                              <div className="text-[12px] font-bold tabular-nums text-gray-900 leading-tight">{formatCompact(item.us_last_complete_week_streams)}</div>
                              <div className="mt-0.5"><PercentPill value={item.us_week_over_week_change_percent} /></div>
                            </td>
                          ) : (
                            <>
                              <td className="px-1 sm:px-4 py-2 sm:py-3.5 text-[11px] sm:text-sm text-right tabular-nums text-gray-800">
                                {formatNumber(item.us_last_complete_week_streams)}
                              </td>
                              <td className="px-1 sm:px-4 py-2 sm:py-3.5 text-center">
                                <PercentPill value={item.us_week_over_week_change_percent} />
                              </td>
                            </>
                          )
                        )}
                        {type === 'song' && (
                          <td className="px-1 sm:px-4 py-2 sm:py-3.5 text-[11px] sm:text-sm text-right tabular-nums text-gray-800">
                            {formatNumber(item.atd_streams)}
                          </td>
                        )}
                        <td className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-3.5 text-[11px] sm:text-sm text-right tabular-nums text-gray-500">
                          {formatPercent(item.audio_streams_percent)}
                        </td>
                        <td className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-3.5 text-[11px] sm:text-sm text-right tabular-nums text-gray-500">
                          {formatPercent(item.video_streams_percent)}
                        </td>
                      </tr>
                      {expandedRow === item.id && containerWidth > 0 && (
                        <tr>
                          <td
                            colSpan={
                              ((type === 'song' || type === 'album') ? (isPgp ? 8 : 9) : (isPgp ? 6 : 7))
                              - (mergeTitleIntoArtist ? 1 : 0)
                              - (isMobile ? 1 : 0)          /* Global pair merged */
                              - (isMobile && !isPgp ? 1 : 0) /* US pair merged */
                            }
                            className="p-0"
                            style={{ position: 'relative' }}
                          >
                            <div
                              style={{ position: 'sticky', left: 0, width: containerWidth, maxWidth: containerWidth, overflow: 'hidden' }}
                              className="space-y-4 px-1 py-2 sm:px-4 sm:py-4 bg-slate-50/60 border-t border-gray-100"
                            >
                              {/* Tabs for artists only */}
                              {type === 'artist' && (
                                <div className="border-b border-gray-200">
                                  <nav className="-mb-px flex space-x-8">
                                    <button
                                      onClick={() => setExpandedRowTab('metrics')}
                                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        expandedRowTab === 'metrics'
                                          ? 'border-blue-500 text-blue-600'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                      }`}
                                    >
                                      Metrics Chart
                                    </button>
                                    <button
                                      onClick={() => setExpandedRowTab('songs')}
                                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        expandedRowTab === 'songs'
                                          ? 'border-blue-500 text-blue-600'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                      }`}
                                    >
                                      Artist Songs
                                    </button>
                                  </nav>
                                </div>
                              )}

                              {/* Content based on selected tab or always show for songs */}
                              {(type === 'song' || type === 'album' || expandedRowTab === 'metrics') && (
                                <>
                                  <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                      <select
                                        value={timeRange}
                                        onChange={e => setTimeRange(Number(e.target.value))}
                                        className="w-1/3 px-4 py-2 border rounded-lg text-sm"
                                      >
                                        <option value={30}>Last 30 days</option>
                                        <option value={90}>Last 90 days</option>
                                        <option value={180}>Last 180 days</option>
                                        <option value={365}>Last year</option>
                                        <option value={9999}>All Time</option>
                                      </select>

                                      <select
                                        value={selectedRegion}
                                        onChange={e => setSelectedRegion(e.target.value)}
                                        className="w-1/3 px-4 py-2 border rounded-lg text-sm"
                                      >
                                        <option value="global">Global</option>
                                        <option value="us">US</option>
                                      </select>

                                      <select
                                        value={viewMode}
                                        onChange={e => setViewMode(e.target.value)}
                                        className="w-1/3 px-4 py-2 border rounded-lg text-sm"
                                      >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                      </select>
                                    </div>

                                    <div className="relative">
                                      <button
                                        onClick={() => setIsMetricsOpen(!isMetricsOpen)}
                                        className="w-full px-4 py-2 border rounded-lg text-sm text-blue-600 bg-white flex justify-between items-center"
                                      >
                                        {selectedMetricLabel}
                                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                                      </button>
                                      
                                      {isMetricsOpen && (
                                        <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg">
                                          {metricOptions.map(option => (
                                            <button
                                              key={option.id}
                                              onClick={() => toggleMetric(option.id)}
                                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                selectedMetrics.includes(option.id) ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                                              }`}
                                            >
                                              {option.label}
                                              {selectedMetrics.includes(option.id) && (
                                                <CheckIcon className="h-5 w-5 text-blue-600" />
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="h-[250px] sm:h-[400px] w-full">
                                    {metrics && (
                                      <MetricsChart
                                        dates={metrics.dates}
                                        metrics={metrics.metrics}
                                        selectedMetrics={selectedMetrics}
                                      />
                                    )}
                                  </div>
                                </>
                              )}

                              {/* Artist Songs Tab Content */}
                              {type === 'artist' && expandedRowTab === 'songs' && (
                                <div className="space-y-4">
                                  <h4 className="text-lg font-medium text-gray-900">Songs by {item.name}</h4>
                                  {artistSongs ? (
                                    <div className="overflow-x-auto min-w-full border-y sm:border border-gray-200/80 sm:rounded-xl">
                                      <table className="w-full">
                                        <colgroup>
                                          <col className="w-[40%] sm:w-[30%]" /> {/* Song */}
                                          <col className="w-[30%] sm:w-[17.5%]" /> {/* Global (merged on mobile) */}
                                          {!isMobile && <col className="w-[17.5%]" />} {/* Global % */}
                                          <col className="w-[30%] sm:w-[17.5%]" /> {/* US (merged on mobile) */}
                                          {!isMobile && <col className="w-[17.5%]" />} {/* US % */}
                                        </colgroup>
                                        <thead className="bg-slate-50/80">
                                          <tr className="border-b border-gray-200">
                                            <th
                                              onClick={() => handleArtistSongsSort('title')}
                                              className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                              Song
                                              <ArtistSongsSortIcon columnKey="title" />
                                            </th>
                                            {isMobile ? (
                                              <th
                                                onClick={(e) => openSongsSortMenu([
                                                  { key: 'global_last_complete_week_streams', label: 'Global Last Week' },
                                                  { key: 'global_week_over_week_change_percent', label: 'Global % Change' },
                                                ], e)}
                                                className="px-1.5 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                                              >
                                                <span className="inline-flex items-center gap-1 justify-end">
                                                  Global
                                                  {(artistSongsSortConfig.key === 'global_last_complete_week_streams' || artistSongsSortConfig.key === 'global_week_over_week_change_percent') && (
                                                    artistSongsSortConfig.order === 'desc' ? <ArrowDownIcon className="h-3 w-3 text-blue-600" /> : <ArrowUpIcon className="h-3 w-3 text-blue-600" />
                                                  )}
                                                  <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                                                </span>
                                              </th>
                                            ) : (
                                              <>
                                                <th onClick={() => handleArtistSongsSort('global_last_complete_week_streams')} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                                  Global Last Week
                                                  <ArtistSongsSortIcon columnKey="global_last_complete_week_streams" />
                                                </th>
                                                <th onClick={() => handleArtistSongsSort('global_week_over_week_change_percent')} className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                                  Global % Change
                                                  <ArtistSongsSortIcon columnKey="global_week_over_week_change_percent" />
                                                </th>
                                              </>
                                            )}
                                            {isMobile ? (
                                              <th
                                                onClick={(e) => openSongsSortMenu([
                                                  { key: 'us_last_complete_week_streams', label: 'US Last Week' },
                                                  { key: 'us_week_over_week_change_percent', label: 'US % Change' },
                                                ], e)}
                                                className="px-1.5 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                                              >
                                                <span className="inline-flex items-center gap-1 justify-end">
                                                  US
                                                  {(artistSongsSortConfig.key === 'us_last_complete_week_streams' || artistSongsSortConfig.key === 'us_week_over_week_change_percent') && (
                                                    artistSongsSortConfig.order === 'desc' ? <ArrowDownIcon className="h-3 w-3 text-blue-600" /> : <ArrowUpIcon className="h-3 w-3 text-blue-600" />
                                                  )}
                                                  <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                                                </span>
                                              </th>
                                            ) : (
                                              <>
                                                <th onClick={() => handleArtistSongsSort('us_last_complete_week_streams')} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                                  US Last Week
                                                  <ArtistSongsSortIcon columnKey="us_last_complete_week_streams" />
                                                </th>
                                                <th onClick={() => handleArtistSongsSort('us_week_over_week_change_percent')} className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                                  US % Change
                                                  <ArtistSongsSortIcon columnKey="us_week_over_week_change_percent" />
                                                </th>
                                              </>
                                            )}
                                            <th onClick={() => handleArtistSongsSort('first_friday_global_streams')} className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap">
                                              First Friday
                                              <ArtistSongsSortIcon columnKey="first_friday_global_streams" />
                                            </th>
                                            <th onClick={() => handleArtistSongsSort('first_friday_us_streams')} className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap">
                                              First Friday US
                                              <ArtistSongsSortIcon columnKey="first_friday_us_streams" />
                                            </th>
                                            <th onClick={() => handleArtistSongsSort('first_week_global_streams')} className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap">
                                              First Week
                                              <ArtistSongsSortIcon columnKey="first_week_global_streams" />
                                            </th>
                                            <th onClick={() => handleArtistSongsSort('first_week_us_streams')} className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap">
                                              First Week US
                                              <ArtistSongsSortIcon columnKey="first_week_us_streams" />
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                          {artistSongs.data.map(song => (
                                            <tr key={song.id} className="hover:bg-blue-50/40 transition-colors">
                                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-sm text-gray-900 truncate">{song.title}</td>
                                              {isMobile ? (
                                                <td className="px-1.5 py-1.5 text-right">
                                                  <div className="text-[12px] font-bold tabular-nums text-gray-900 leading-tight">{formatCompact(song.global_last_complete_week_streams)}</div>
                                                  <div className="mt-0.5"><PercentPill value={song.global_week_over_week_change_percent} /></div>
                                                </td>
                                              ) : (
                                                <>
                                                  <td className="px-4 py-3 text-sm text-gray-800 text-right tabular-nums">{formatNumber(song.global_last_complete_week_streams)}</td>
                                                  <td className="px-4 py-3 text-center"><PercentPill value={song.global_week_over_week_change_percent} /></td>
                                                </>
                                              )}
                                              {isMobile ? (
                                                <td className="px-1.5 py-1.5 text-right">
                                                  <div className="text-[12px] font-bold tabular-nums text-gray-900 leading-tight">{formatCompact(song.us_last_complete_week_streams)}</div>
                                                  <div className="mt-0.5"><PercentPill value={song.us_week_over_week_change_percent} /></div>
                                                </td>
                                              ) : (
                                                <>
                                                  <td className="px-4 py-3 text-sm text-gray-800 text-right tabular-nums">{formatNumber(song.us_last_complete_week_streams)}</td>
                                                  <td className="px-4 py-3 text-center"><PercentPill value={song.us_week_over_week_change_percent} /></td>
                                                </>
                                              )}
                                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-sm text-gray-800 text-right tabular-nums">{formatNumber(song.first_friday_global_streams)}</td>
                                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-sm text-gray-500 text-right tabular-nums">{formatNumber(song.first_friday_us_streams)}</td>
                                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-sm text-gray-800 text-right tabular-nums">{formatNumber(song.first_week_global_streams)}</td>
                                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-sm text-gray-500 text-right tabular-nums">{formatNumber(song.first_week_us_streams)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="text-center py-8 text-gray-500">Loading songs...</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Expanded Row Content - Positioned absolutely */}
        
      </div>

      <div ref={observerTarget} className="h-4" />

      {isFetchingNextPage && (
        <div className="text-center py-4">Loading more...</div>
      )}

      {sortMenu && (
        <MergedSortMenu
          options={sortMenu.options}
          anchorRect={sortMenu.rect}
          sortConfig={sortConfig}
          onSelect={applyMergedSort}
          onClose={() => setSortMenu(null)}
        />
      )}

      {songsSortMenu && (
        <MergedSortMenu
          options={songsSortMenu.options}
          anchorRect={songsSortMenu.rect}
          sortConfig={artistSongsSortConfig}
          onSelect={applySongsSort}
          onClose={() => setSongsSortMenu(null)}
        />
      )}
    </div>
  );
} 